/**
 * Marketing state management — URL-driven.
 *
 * Same pattern as useProductState but for marketing reports.
 * All configuration lives in URL query params for deep-linking.
 *
 * Example URL:
 *   /marketing/new?breakdown=campaign_name&metrics=cost,revenue,installs,cpi,roas&preset=last_7_days
 */
import { useCallback, useMemo } from 'react';
import { resolveDateRange } from '@/lib/dates';
import { useBulkUrlState } from '@/hooks/useUrlState';
import { DEFAULT_METRICS } from './marketing.constants';

export interface MarketingState {
  // Always 'marketing'
  type: 'marketing';
  // Row breakdown
  breakdown: string;
  // Selected metric keys (comma-separated in URL)
  metrics: string[];
  // Date range
  start: string;
  end: string;
  preset: string;
  // Comparison date range (pivot only)
  cmpStart: string;
  cmpEnd: string;
  cmpPreset: string;
  // View
  chart: 'ts' | 'pivot';
  interval: string;
  // Cumulative (time series only)
  cumulative: boolean;
  // Filters (base64 JSON, same encoding as product reports)
  filters: string;
}

const DEFAULTS: MarketingState = {
  type: 'marketing',
  breakdown: 'campaign_name',
  metrics: DEFAULT_METRICS,
  start: '',
  end: '',
  preset: 'last_28_days',
  cmpStart: '',
  cmpEnd: '',
  cmpPreset: '',
  chart: 'pivot',
  interval: 'd',
  cumulative: false,
  filters: '',
};

/**
 * Read and write marketing config from/to URL query params.
 */
export function useMarketingState() {
  const { state: rawState, update, reset, searchParams } = useBulkUrlState(DEFAULTS, {
    arrayKeys: ['metrics'],
    booleanKeys: ['cumulative'],
    replace: true,
    preserveOnReset: { type: 'marketing' },
  });

  // Fix preset: if custom start/end dates are in the URL, don't fall back to a default preset
  const state = useMemo<MarketingState>(() => {
    const preset = searchParams.has('start') && !searchParams.has('preset')
      ? ''
      : (rawState.preset as string);
    return {
      ...rawState,
      type: 'marketing' as const,
      preset,
      chart: (rawState.chart || 'pivot') as MarketingState['chart'],
    } as MarketingState;
  }, [rawState, searchParams]);

  /** Resolved start/end dates from preset or explicit dates */
  const dates = useMemo(() => {
    return resolveDateRange(state.preset, state.start, state.end);
  }, [state.preset, state.start, state.end]);

  /** Resolved comparison dates (null when not set) */
  const comparisonDates = useMemo(() => {
    if (!state.cmpPreset && !state.cmpStart) return null;
    return resolveDateRange(state.cmpPreset, state.cmpStart, state.cmpEnd);
  }, [state.cmpPreset, state.cmpStart, state.cmpEnd]);

  // Auto-switch interval to hours when a single day is selected
  const effectiveInterval = useMemo(() => {
    if (dates.startDate === dates.endDate && dates.startDate) {
      return 'h';
    }
    return state.interval;
  }, [dates.startDate, dates.endDate, state.interval]);

  const canRun = true;

  /** Build the API request body from current state */
  const toApiPayload = useCallback(() => {
    const payload: Record<string, unknown> = {
      startDate: dates.startDate,
      endDate: dates.endDate,
      rowBreakdown: state.breakdown,
      selectedMetrics: state.metrics,
      timeInterval: expandInterval(effectiveInterval),
      cumulative: state.cumulative,
      filters: state.filters ? decodeFilters(state.filters) : [],
    };
    return payload;
  }, [state, dates, effectiveInterval]);

  return { state, update, reset, canRun, toApiPayload, dates, comparisonDates, searchParams };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function expandInterval(short: string): string {
  const map: Record<string, string> = { h: 'hours', d: 'days', w: 'weeks', m: 'months' };
  return map[short] || short;
}

/**
 * Decode base64-encoded filters from URL.
 * Supports both new grouped format { groups: [...] } and legacy flat array.
 * Preserves group structure including conditionOperator and groupOperator.
 */
function decodeFilters(encoded: string): Array<{
  field: string;
  value: string;
  comparison: string;
  _group?: number;
  _conditionOperator?: 'AND' | 'OR';
  _groupOperator?: 'AND' | 'OR';
}> {
  try {
    const parsed = JSON.parse(atob(encoded));

    // New grouped format: { groups: [...], groupOperator? }
    if (parsed && Array.isArray(parsed.groups)) {
      const conditions: Array<{
        field: string;
        value: string;
        comparison: string;
        _group?: number;
        _conditionOperator?: 'AND' | 'OR';
        _groupOperator?: 'AND' | 'OR';
      }> = [];
      const groupOperator: 'AND' | 'OR' = parsed.groupOperator ?? 'OR';
      for (let gIdx = 0; gIdx < parsed.groups.length; gIdx++) {
        const group = parsed.groups[gIdx];
        if (!Array.isArray(group.conditions)) continue;
        const condOp: 'AND' | 'OR' = group.conditionOperator ?? 'AND';
        for (const c of group.conditions) {
          if (c.field && c.value) {
            conditions.push({
              field: c.field,
              value: c.value,
              comparison: c.comparison || c.operator || '=',
              _group: gIdx,
              _conditionOperator: condOp,
              _groupOperator: groupOperator,
            });
          }
        }
      }
      return conditions;
    }

    // Legacy flat array
    if (Array.isArray(parsed)) {
      return (parsed as Array<{ field: string; operator?: string; comparison?: string; value: string }>)
        .filter((f) => f.field && f.value)
        .map((f) => ({
          field: f.field,
          value: f.value,
          comparison: f.comparison || f.operator || '=',
        }));
    }

    return [];
  } catch {
    return [];
  }
}
